<script lang="ts">
  import { storyblokEditable } from '@storyblok/svelte';
  import { castColor, preHeadingColors } from '@athena-lib/colors';
  import { PageSection, InnerPageSection } from '@athena-lib/Page';
  import type { MediaBannerPageSectionBlock } from '@storyblok-types';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { BaseHeading, type HeadingSize } from '@athena-lib/Text';
  import clsx from 'clsx';
  import { ButtonLink, type ButtonColor } from '@athena-lib/Button';
  import { Text } from '@athena-lib/Text';
  import { Link } from '@athena-lib/Link';
  import { TrustPilotFullStar, TrustPilotHalfStar } from '@athena-lib/Icons';
  import { getStars } from '@athena-lib/trustpilot';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { parseHeadingLevel } from '@athena-lib/Text/BaseHeading/types';
  import { RichText } from '../RichText';
  import { isRichTextEmpty } from '../RichText/types';
  import {
    alignClasses,
    containerAlignClasses,
    onTrustPilotClick,
    type Alignment,
    type VerticalAlignment,
  } from './shared/page-section-utils';
  import { getBackgroundFocalPointClasses } from './shared/image-utils';
  import AddToCartButton from './AddToCartButton.svelte';
  import { getUrlByLinkType } from '$lib/utilities/storyblok';
  import type { WithTrustpilotServiceReview } from '$lib/server/expanders/withTrustpilotReview';

  const {
    blok,
    isHomePage,
    blockIndex,
  }: {
    blok: WithTrustpilotServiceReview<MediaBannerPageSectionBlock>;
    isHomePage?: boolean;
    blockIndex?: number;
  } = $props();

  const heightClass = $derived.by(() => {
    switch (blok.height) {
      case 'xsmall':
        return 'min-h-[360px]';
      case 'small':
        return 'min-h-[440px]';
      case 'medium':
        return 'min-h-[580px]';
      case 'large':
        return 'min-h-[680px] md:min-h-[860px]';
      case 'xlarge':
        return 'min-h-[920px]';
    }
  });
  const verticalAlign = $derived.by(() => {
    switch (blok.desktopVerticalAlignment as VerticalAlignment) {
      case 'top':
        return 'items-center md:items-start';
      default:
      case 'middle':
        return 'items-center md:items-center';
      case 'bottom':
        return 'items-center md:items-end';
    }
  });
  const horizontalAlign = $derived.by(() => {
    switch (blok.desktopAlignment as Alignment) {
      case 'left':
        return 'justify-center md:justify-start';
      default:
      case 'center':
        return 'justify-center md:justify-center';
      case 'right':
        return 'justify-center md:justify-end';
    }
  });

  const stars = $derived(
    blok.trustPilotReviewsApiResponse
      ? getStars(blok.trustPilotReviewsApiResponse.starsAverage)
      : [],
  );

  const translations = getCommonTranslations();

  const responsivFullWidthWidth = $derived({
    sm: blok.mobileFullWidth ? 'full' : 'large',
    md: blok.desktopFullWidth ? 'full' : 'large',
    lg: blok.desktopFullWidth ? 'full' : 'large',
  } as const);
  const responsivFullWidth = $derived({
    sm: blok.mobileFullWidth ?? true,
    md: blok.desktopFullWidth ?? true,
    lg: blok.desktopFullWidth ?? true,
  });
</script>

{#snippet texts(inheritColors: boolean)}
  <Stack class="max-w-text-max" space="large">
    {#if blok.headingText || !isRichTextEmpty(blok.description)}
      <StackItem>
        <Stack space="gutter">
          {#if blok.headingText}
            <StackItem>
              <BaseHeading
                level={parseHeadingLevel(blok.headingTag) ?? 2}
                size={blok.headingSize as HeadingSize}
                color={inheritColors ? undefined : castColor(blok.headingColor)}
              >
                {#if blok.preHeading}
                  <span
                    class={clsx(
                      'block text-xl',
                      preHeadingColors[castColor(blok.preHeadingColor) ?? 'dark'],
                    )}
                  >
                    {blok.preHeading}
                  </span>
                {/if}

                {blok.headingText}
              </BaseHeading>
            </StackItem>
          {/if}
          {#if blok.showTrustPilotScore && blok.trustPilotReviewsApiResponse}
            <div
              class={clsx(
                'flex flex-col gap-3',
                alignClasses['mobile'][(blok.mobileTextAlignment ?? 'left') as Alignment],
                alignClasses['desktop'][(blok.desktopTextAlignment ?? 'left') as Alignment],
              )}
            >
              <Link
                href={blok.trustPilotReviewsApiResponse.reviewsUrl}
                isExternal
                class={clsx('flex items-center gap-3')}
                onClick={onTrustPilotClick}
              >
                <div class="flex">
                  {#each stars as star}
                    <div class="p-[1px]">
                      {#if star === 'full'}
                        <TrustPilotFullStar sizeClass="w-6 h-6" />
                      {:else}
                        <TrustPilotHalfStar sizeClass="w-6 h-6" />
                      {/if}
                    </div>
                  {/each}
                </div>
                <Text
                  color={inheritColors ? undefined : castColor(blok.descriptionColor)}
                  size="small"
                >
                  <strong>{blok.trustPilotReviewsApiResponse.starsAverage}</strong>
                  {getCommonTranslations().outOf}
                  <!-- TODO: Should this formatting be localized? -->
                  <strong>
                    {Intl.NumberFormat('en-US').format(
                      blok.trustPilotReviewsApiResponse.allReviews,
                    )}
                  </strong>
                  {translations.reviews.toLowerCase()}
                </Text>
              </Link>
            </div>
          {/if}
          {#if !isRichTextEmpty(blok.description)}
            <StackItem>
              <RichText
                text={blok.description}
                color={inheritColors ? undefined : castColor(blok.descriptionColor)}
                size={{ sm: 'medium', md: 'xlarge' }}
              />
            </StackItem>
          {/if}
        </Stack>
      </StackItem>
    {/if}
    {#if blok.ctaButtonText || blok.ctaSecondaryButtonText}
      <StackItem>
        <StackItem>
          <div
            class={clsx(
              'flex flex-col gap-6 md:flex-row',
              containerAlignClasses['mobile'][(blok.mobileTextAlignment ?? 'left') as Alignment],
              containerAlignClasses['desktop'][(blok.desktopTextAlignment ?? 'left') as Alignment],
            )}
          >
            {#if blok.ctaButtonText}
              {#if blok.addToCart && blok.productType}
                <AddToCartButton
                  color={(blok.ctaButtonColor as ButtonColor) ?? 'dark'}
                  productId={blok.productId}
                  buttonText={blok.ctaButtonText}
                  productType={blok.productType}
                  size={{ sm: 'medium', md: 'large' }}
                  class={blok.ctaButtonExtraClasses}>{blok.ctaButtonText}</AddToCartButton
                >
              {:else}
                <ButtonLink
                  link={getUrlByLinkType(blok.ctaButtonLink)}
                  color={(blok.ctaButtonColor as ButtonColor) ?? 'dark'}
                  size={{ sm: 'medium', md: 'large' }}
                  class={blok.ctaButtonExtraClasses}
                >
                  {blok.ctaButtonText}
                </ButtonLink>{/if}
            {/if}

            {#if blok.ctaSecondaryButtonText}
              {#if blok.addToCartSecondary && blok.productTypeSecondary}
                <AddToCartButton
                  color={(blok.ctaSecondaryButtonColor as ButtonColor) ?? 'dark'}
                  productId={blok.productIdSecondary}
                  buttonText={blok.ctaSecondaryButtonText}
                  productType={blok.productTypeSecondary}
                  class={blok.ctaSecondaryButtonExtraClasses}
                  size={{ sm: 'medium', md: 'large' }}
                  >{blok.ctaSecondaryButtonText}</AddToCartButton
                >
              {:else}
                <ButtonLink
                  link={getUrlByLinkType(blok.ctaSecondaryButtonLink)}
                  class={blok.ctaSecondaryButtonExtraClasses}
                  color={(blok.ctaSecondaryButtonColor as ButtonColor) ?? 'dark'}
                  size={{ sm: 'medium', md: 'large' }}
                >
                  {blok.ctaSecondaryButtonText}
                </ButtonLink>
              {/if}
            {/if}
          </div>
        </StackItem>
      </StackItem>
    {/if}
  </Stack>
{/snippet}

<PageSection
  id={blok.elementId}
  action={storyblokEditable}
  actionArg={blok}
  width={responsivFullWidthWidth}
  backgroundColor={blok.backgroundColor ?? 'light'}
  backgroundMediaUrl={blok.mobileBackgroundImage?.filename
    ? {
        sm: blok.mobileBackgroundImage.filename,
        md: blok.backgroundImage?.filename ?? blok.mobileBackgroundImage.filename,
        lg: blok.backgroundImage?.filename ?? blok.mobileBackgroundImage.filename,
      }
    : blok.backgroundImage?.filename}
  backgroundMediaFocalPointClass={blok.backgroundImage &&
    getBackgroundFocalPointClasses(blok.backgroundImage)}
  fullWidthBackgroundMedia={responsivFullWidth}
  fullWidthBackgroundColor={responsivFullWidth}
  xSpace="none"
  ySpace={{ top: blok.topPadding ?? 'none', bottom: blok.bottomPadding ?? 'none' }}
  innerClass={clsx('flex', heightClass, verticalAlign)}
>
  <InnerPageSection>
    {#if isHomePage && blockIndex === 0}
      <div class="h-16 md:h-20"></div>
    {/if}
    <div
      class={clsx(
        'flex',
        blok.mobileTextBelowImage && 'hidden md:flex',
        alignClasses['mobile'][(blok.mobileTextAlignment ?? 'left') as Alignment],
        alignClasses['desktop'][(blok.desktopTextAlignment ?? 'left') as Alignment],
        horizontalAlign,
        'pb-4 pt-4',
      )}
    >
      {@render texts(false)}
    </div>
  </InnerPageSection>
</PageSection>
{#if blok.mobileTextBelowImage}
  <PageSection
    ySpace={{
      top: 'small',
      bottom: blok.bottomPadding ?? 'gutter',
    }}
    class="block md:hidden"
    xSpace={blok.mobileFullWidth ? 'none' : undefined}
    innerClass={clsx(alignClasses['mobile'][(blok.mobileTextAlignment ?? 'left') as Alignment])}
  >
    <InnerPageSection>
      {@render texts(true)}
    </InnerPageSection>
  </PageSection>
{/if}
